import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { from, map, Observable, forkJoin, Subscription, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { Report } from '../model/report';
import { UrlService } from "./url-service";

@Injectable({
  providedIn: 'root'
})
export class AdvancedUserService {
  private http = inject(HttpClient);
  private urlService = inject(UrlService);

  private advancedUserUrl: string = environment.advancedUserUrl;
  private qrCodeRequestSubscription?: Subscription;
  private activeQrRequestId = 0;

  private qrModalStateSignal = signal({
    visible: false,
    shortLink: '',
    qrCodeImageUrl: null as string | null,
  });

  public readonly qrModalState = this.qrModalStateSignal.asReadonly();


  public showQrCodeWindow(report: Report): void {
    const requestId = this.startNewQrRequest();
    const query = this.buildQrQuery(report);

    this.replaceQrModalState({
      visible: false,
      shortLink: '',
      qrCodeImageUrl: null,
    });

    this.qrCodeRequestSubscription = this.loadQrModalData(query)
      .subscribe({
        next: (modalData) => {
          if (!this.isCurrentRequest(requestId)) {
            return;
          }

          this.replaceQrModalState({
            visible: true,
            shortLink: modalData.shortLink,
            qrCodeImageUrl: modalData.qrCodeImageUrl,
          });
        },
        error: () => {
          if (!this.isCurrentRequest(requestId)) {
            return;
          }
          this.hideQrCodeWindow();
        }
      });
  }

  public isAdvancedUser(): Observable<boolean> {
    return this.http.get<boolean>(this.advancedUserUrl);
  }

  public hideQrCodeWindow(): void {
    this.startNewQrRequest();
    this.replaceQrModalState({
      visible: false,
      shortLink: '',
      qrCodeImageUrl: null,
    });
  }

  private loadQrModalData(query: string): Observable<{ shortLink: string; qrCodeImageUrl: string }> {
    return forkJoin({
      shortLink: this.findShortlink(query),
      qrBlob: this.findQrCodeBlob(query)
    }).pipe(
      switchMap(({ shortLink, qrBlob }) =>
        from(this.blobToDataUrl(qrBlob)).pipe(
          map((qrCodeImageUrl) => ({
            shortLink: (shortLink ?? '').trim(),
            qrCodeImageUrl
          }))
        )
      )
    );
  }

  private findShortlink(parameter: string): Observable<string> {
    return this.http.get(environment.shortlinkUrl + parameter, { responseType: 'text' });
  }

  private findQrCodeBlob(parameter: string): Observable<Blob> {
    return this.http.get(environment.qrlinkUrl + parameter, { responseType: 'blob' });
  }

  private buildQrQuery(report: Report): string {
    return "?" + this.urlService.createQueryString(report);
  }


  private startNewQrRequest(): number {
    this.activeQrRequestId++;
    this.qrCodeRequestSubscription?.unsubscribe();
    this.qrCodeRequestSubscription = undefined;
    return this.activeQrRequestId;
  }

  private isCurrentRequest(requestId: number): boolean {
    return requestId === this.activeQrRequestId;
  }

  private replaceQrModalState(nextState: { visible: boolean; shortLink: string; qrCodeImageUrl: string | null }): void {
    this.qrModalStateSignal.set(nextState);
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string' && result.length > 0) {
          resolve(result);
          return;
        }
        reject(new Error('EMPTY_QR_DATA_URL'));
      };
      reader.onerror = () => reject(new Error('QR_DATA_URL_READ_ERROR'));
      reader.readAsDataURL(blob);
    });
  }
}
